import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Music,
  Tv,
  FileCheck,
  Users,
  Play,
  Building2,
  Smile,
  Heart,
  Smartphone,
  QrCode,
  Sparkles,
  Download,
  Scale,
  ChevronRight,
} from 'lucide-react';

export default function FeaturesPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('features.title', 'Features')}
            </h1>
            <p className="text-gray-400 text-lg">
              {t('features.subtitle', 'Everything you need for your business music')}
            </p>
          </div>

          {/* Feature Cards */}
          <div className="space-y-6">
            {/* Music Player Section */}
            <section className="backdrop-blur-xl bg-gradient-to-br from-primary-500/10 to-primary-600/5 border border-primary-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                  <Music className="w-6 h-6 text-primary-400" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  {t('features.player.title', 'Music Player')}
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <FeatureItem
                  icon={<Play className="w-5 h-5" />}
                  text={t('features.player.infiniteQueue', 'Infinite playback - music never stops')}
                />
                <FeatureItem
                  icon={<Building2 className="w-5 h-5" />}
                  text={t('features.player.profiles', 'Business profiles tailored for your industry')}
                />
                <FeatureItem
                  icon={<Smile className="w-5 h-5" />}
                  text={t('features.player.moods', 'Mood selection for the right atmosphere')}
                />
                <FeatureItem
                  icon={<Heart className="w-5 h-5" />}
                  text={t('features.player.favorites', 'Save your favorite tracks')}
                />
              </div>
            </section>

            {/* TV App Section */}
            <section className="backdrop-blur-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Tv className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {t('features.tv.title', 'TV App & Remote')}
                  </h2>
                  <p className="text-sm text-gray-400">
                    {t('features.tv.description', 'Play music on your business TV with remote control from your phone')}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <FeatureItem
                  icon={<Tv className="w-5 h-5" />}
                  text={t('features.tv.smartTv', 'Works on any Smart TV browser')}
                />
                <FeatureItem
                  icon={<QrCode className="w-5 h-5" />}
                  text={t('features.tv.qrLogin', 'Quick QR code connection')}
                />
                <FeatureItem
                  icon={<Smartphone className="w-5 h-5" />}
                  text={t('features.tv.remote', 'Control playback from your phone')}
                />
                <FeatureItem
                  icon={<Sparkles className="w-5 h-5" />}
                  text={t('features.tv.visualizer', 'Beautiful music visualizer')}
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/tv/app"
                  target="_blank"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl transition-all"
                >
                  <Tv className="w-4 h-4" />
                  {t('features.tv.openApp', 'Open TV App')}
                  <ChevronRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/tv/remote"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/10"
                >
                  <Smartphone className="w-4 h-4" />
                  {t('features.tv.openRemote', 'Open Remote')}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </section>

            {/* License Section */}
            <section className="backdrop-blur-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <FileCheck className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  {t('features.license.title', 'License & Legal')}
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <FeatureItem
                  icon={<Download className="w-5 h-5" />}
                  text={t('features.license.certificate', 'Download your license certificate')}
                />
                <FeatureItem
                  icon={<Scale className="w-5 h-5" />}
                  text={t('features.license.noFees', 'No licensing fees or royalties')}
                />
              </div>
            </section>

            {/* Affiliate Section */}
            <section className="backdrop-blur-xl bg-gradient-to-br from-orange-500/10 to-yellow-500/5 border border-orange-500/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {t('features.affiliate.title', 'Affiliate Program')}
                    </h2>
                    <p className="text-sm text-gray-400">
                      {t('features.affiliate.description', 'Earn 10% recurring commission')}
                    </p>
                  </div>
                </div>
                <Link
                  to="/affiliate/apply"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-xl transition-all"
                >
                  {t('features.affiliate.learnMore', 'Learn More')}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
  );
}

function FeatureItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-3 text-gray-300">
      <div className="text-white/60 flex-shrink-0">{icon}</div>
      <span>{text}</span>
    </div>
  );
}
